import { ReportReaderProps } from './types';
import { APIReportFilters } from '@ardoq/api-types';
import { SearchResultsTable } from './SearchResultsTable';
import { ExportButton } from './ExportButton';
import { ErrorPage } from './ErrorPage';
import { getDataSourceLabel } from './utils';
import { getActiveBarFilters } from './filter/filterUtils';
import { Box, FlexBox } from '@ardoq/layout';
import { ReportTrackingContext } from './tracking/ReportTrackingContext';
import {
  ActionableNewFeatureNotification,
  CopyEntityURLButton,
  SearchAndFilterBar,
} from '@ardoq/snowflakes';
import { Island, PageBody, PageHeaderIsland } from '@ardoq/page-layout';
import { ReportEventLocations } from './tracking/types';
import { trackOpenReportReader } from './tracking/utils';
import { useEffect, useRef, useState } from 'react';
import { ReportPageWrapper } from './ReportPageWrapper';
import { ErrorNotification, NotificationWrapper } from '@ardoq/status-ui';
import { ReportFilterSidebar } from './filter/ReportFilterSidebar';
import { toUsersDataForRenderersById } from '@ardoq/renderers';
import { isEmpty } from 'lodash';
import { LoadingOverlay } from '@ardoq/loader';

const DEFAULT_LOADING_MESSAGE = 'Loading report';
// With content above the report, the page scrolls rather than squeezing the report below this height.
const MIN_CONTENT_HEIGHT_WITH_HEADER = '470px';
const DEFAULT_EXCEL_EXPORT_ERROR =
  'Excel export failed due to a technical issue, please try again or contact support if the issue persists.';

export const ReportReader = ({
  isEntityNavigationDisabled,
  getComponentLinkHandler,
  getComponentNavigationOptions,
  currentUserLocale,
  openHome,
  isExportingDisabled = false,
  isCaptureMode = false,
  trackEvent,
  getHasComponentReadPermission,
  hasExcelExportFailed,
  commands,
  sortFilterPagination: { sort, filters, pagination, page },
  isSearchBeingExecuted,
  searchResults,
  filterableColumns,
  reportId,
  solutionMetadata,
  addFilterButtonOptions,
  filtersToRender,
  newFeatureNotificationsProps,
  aggregatesResultsState,
  isAggregatesBeingExecuted,
  isFilteringDisabled,
  error,
  isLoading,
  aiButtonProps,
  labels,
  i18nLocale,
  children,
  ...props
}: ReportReaderProps) => {
  const loadingMessage = labels?.loadingMessage ?? DEFAULT_LOADING_MESSAGE;
  const excelExportError =
    labels?.excelExportError ?? DEFAULT_EXCEL_EXPORT_ERROR;
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const trackedReportId = useRef<string | null>(null);

  useEffect(() => {
    if (!solutionMetadata || trackedReportId.current === reportId) return;
    trackedReportId.current = reportId;
    trackOpenReportReader(
      trackEvent,
      props.reportReaderLocation,
      reportId,
      solutionMetadata
    );
  }, [reportId, trackEvent, props.reportReaderLocation, solutionMetadata]);

  const showPageHeaderIsland = Boolean(
    props.reportReaderLocation !== ReportEventLocations.ARDOQ ||
    searchResults?.description ||
    hasExcelExportFailed
  );

  return (
    <ReportTrackingContext.Provider
      value={{ trackEvent, reportReaderLocation: props.reportReaderLocation }}
    >
      <ReportPageWrapper $isCaptureMode={isCaptureMode}>
        <PageBody
          isMain
          alignIslandsToLeft
          preventScroll={!isCaptureMode}
          rightContent={
            isFilterSidebarOpen && !isFilteringDisabled && !isCaptureMode ? (
              <ReportFilterSidebar
                filtersToRender={filtersToRender}
                addFilterButtonOptions={addFilterButtonOptions}
                onCloseClick={() => setIsFilterSidebarOpen(false)}
                labels={labels?.filterSidebar}
              />
            ) : undefined
          }
          minContentHeight={
            children ? MIN_CONTENT_HEIGHT_WITH_HEADER : undefined
          }
          headerContent={
            <>
              {children && (
                <Box
                  paddingX="large"
                  paddingTop="large"
                  backgroundColor="surfaceDefaultSubtle"
                >
                  {children}
                </Box>
              )}
              {searchResults?.error && (
                <FlexBox paddingX={'xlarge'} paddingTop={'medium'}>
                  <ErrorNotification>{searchResults.error}</ErrorNotification>
                </FlexBox>
              )}
            </>
          }
        >
          {!isEmpty(newFeatureNotificationsProps) && (
            <NotificationWrapper>
              {newFeatureNotificationsProps?.map((notificationProps, i) => (
                <ActionableNewFeatureNotification
                  key={i}
                  {...notificationProps}
                />
              ))}
            </NotificationWrapper>
          )}

          {error && (
            <ErrorPage
              error={error}
              openHome={openHome}
              labels={labels?.errorPage}
              i18nLocale={i18nLocale}
            />
          )}
          {isLoading && <LoadingOverlay message={loadingMessage} />}
          {!error && !isLoading && searchResults && (
            <>
              {showPageHeaderIsland && (
                <PageHeaderIsland
                  title={
                    props.reportReaderLocation !== ReportEventLocations.ARDOQ
                      ? searchResults.name
                      : null
                  }
                  description={searchResults.description ?? undefined}
                  buttons={
                    props.reportReaderLocation !==
                      ReportEventLocations.ARDOQ && (
                      <>
                        {props.canDrilldownIntoDiscover &&
                          props.copyUrlButtonProps && (
                            <CopyEntityURLButton
                              {...props.copyUrlButtonProps}
                            />
                          )}
                        {!isExportingDisabled && props.exportButtonProps && (
                          <ExportButton
                            {...props.exportButtonProps}
                            commands={commands}
                            labels={labels?.exportButton}
                          />
                        )}
                      </>
                    )
                  }
                  errorMessage={
                    hasExcelExportFailed ? excelExportError : undefined
                  }
                  maxWidth="100%"
                  showExpandDescriptionButton
                  isDescriptionExpanded={isCaptureMode ? true : undefined}
                />
              )}
              <Island
                bodySpacing="medium"
                preventScroll={!isCaptureMode}
                maxWidth="100%"
              >
                {!isFilteringDisabled &&
                !isCaptureMode &&
                (!isEmpty(filterableColumns) || !isEmpty(filters)) ? (
                  <SearchAndFilterBar
                    searchDisabled
                    activeFilters={getActiveBarFilters(
                      filters ?? {},
                      filterableColumns
                    )}
                    removeFilter={(_: unknown, type: keyof APIReportFilters) =>
                      commands.removeFilterExpression(type)
                    }
                    clearAllFilters={commands.clearAllFilters}
                    onFilterButtonClick={() => setIsFilterSidebarOpen(true)}
                    barContainerProps={{
                      $padding: 'none',
                      $containerHeight: '36px',
                      $disableBottomBorder: true,
                    }}
                    aiButtonProps={aiButtonProps}
                  />
                ) : null}
                <SearchResultsTable
                  users={toUsersDataForRenderersById(
                    searchResults?.scopeData?.users ?? []
                  )}
                  dataSourceLabel={getDataSourceLabel(
                    searchResults.datasource,
                    labels?.dataSourceLabels
                  )}
                  searchResults={searchResults}
                  aggregatesResultsState={aggregatesResultsState}
                  builderSelectedAggregate={searchResults.selectedAggregate}
                  isEntityNavigationDisabled={isEntityNavigationDisabled}
                  getComponentLinkHandler={getComponentLinkHandler}
                  getComponentNavigationOptions={getComponentNavigationOptions}
                  currentUserLocale={currentUserLocale}
                  isSearchBeingExecuted={isSearchBeingExecuted}
                  isAggregatesBeingExecuted={isAggregatesBeingExecuted}
                  isInteractive
                  isCaptureMode={isCaptureMode}
                  showAggregate
                  hasComponentReadPermission={getHasComponentReadPermission}
                  pageAndPaginationState={[
                    { pagination, page },
                    commands.setPageAndPagination,
                  ]}
                  sort={sort}
                  setSort={commands.setSort}
                  labels={labels?.searchResultsTable}
                />
              </Island>
            </>
          )}
        </PageBody>
      </ReportPageWrapper>
    </ReportTrackingContext.Provider>
  );
};
